import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { useRuntimeVariables } from 'app-kit'
import { Button } from 'designSystem/components/Button'
import { Spinner } from 'designSystem/components/Spinner'
import { Text } from 'designSystem/components/Text'
import { Textarea } from 'designSystem/components/Textarea'
import { useGeckoAuth } from 'features/auth'
import { BugReportAgentModeField } from 'features/knowledgeDocumentViewer/components/bugReport/BugReportAgentModeField'
import { BugReportPriorityField } from 'features/knowledgeDocumentViewer/components/bugReport/BugReportPriorityField'
import { BugReportScreenshots } from 'features/knowledgeDocumentViewer/components/bugReport/BugReportScreenshots'
import { BugReportTypeField } from 'features/knowledgeDocumentViewer/components/bugReport/BugReportTypeField'
import { useBugReportScreenshots } from 'features/knowledgeDocumentViewer/components/bugReport/useBugReportScreenshots'
import {
  screenshotFilesFromClipboard,
  screenshotPayload,
} from 'features/knowledgeDocumentViewer/utils/bugReportScreenshots'
import {
  type PopupLayout,
  RESIZE_DIRECTIONS,
  resizeBugReportPopup,
  type ResizeDirection,
} from 'features/knowledgeDocumentViewer/utils/resizeBugReportPopup'
import {
  type BugReportAgentMode,
  type BugReportPriority,
  type BugReportType,
  buildViewerBugReportSlackPayload,
  buildViewerBugReportUserBody,
  DEFAULT_BUG_REPORT_CLASSIFICATION,
  type ViewerBugReportContext,
} from 'features/knowledgeDocumentViewer/utils/viewerBugReport'
import { useGeckoPermissions } from 'features/permissions'
import { Flag, X } from 'lucide-react'
import {
  type ClipboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import styled, { css } from 'styled-components'
import { PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

const DEFAULT_POPUP_WIDTH_PX = 32 * 16
const DEFAULT_POPUP_HEIGHT_PX = 40 * 16
const MIN_POPUP_WIDTH_PX = 22 * 16
const MIN_POPUP_HEIGHT_PX = 20 * 16
const POPUP_VIEWPORT_MARGIN_PX = 16

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

function getDefaultPopupLayout(
  width: number,
  height: number
): { left: number; top: number; width: number; height: number } {
  const maxWidth = Math.max(0, window.innerWidth - POPUP_VIEWPORT_MARGIN_PX * 2)
  const maxHeight = Math.max(
    0,
    window.innerHeight - POPUP_VIEWPORT_MARGIN_PX * 2
  )
  const nextWidth = clamp(
    width,
    Math.min(MIN_POPUP_WIDTH_PX, maxWidth),
    maxWidth
  )
  const nextHeight = clamp(
    height,
    Math.min(MIN_POPUP_HEIGHT_PX, maxHeight),
    maxHeight
  )
  return {
    width: nextWidth,
    height: nextHeight,
    left: Math.round((window.innerWidth - nextWidth) / 2),
    top: Math.round((window.innerHeight - nextHeight) / 2),
  }
}

function computePointerDelta(
  origin: number,
  startClient: number,
  client: number
) {
  return origin + client - startClient
}

const BUG_REPORT_TYPE_PLACEHOLDERS: Record<BugReportType, MessageDescriptor> = {
  bug: msg`Tell us what went wrong...`,
  feature: msg`Tell us about the feature you would like...`,
  improvement: msg`Tell us what we could improve...`,
}

const BUG_REPORT_TYPE_INPUT_LABELS: Record<BugReportType, MessageDescriptor> = {
  bug: msg`Bug details`,
  feature: msg`Feature request details`,
  improvement: msg`Improvement details`,
}

type DocumentsBugReportButtonProps = {
  buildContext: (action: string) => ViewerBugReportContext
  onRecordAction?: (action: string) => void
  onSubmitSuccess?: () => void
  openActionLabel?: string
  dialogTitle?: string
  renderTrigger?: (props: {
    disabled: boolean
    onClick: () => void
  }) => ReactNode
}

const FloatingBugReportPopup = styled.div.withConfig({
  shouldForwardProp: (prop: string) =>
    prop !== '$left' &&
    prop !== '$top' &&
    prop !== '$width' &&
    prop !== '$height',
})<{
  $left: number
  $top: number
  $width: number
  $height: number
}>`
  background: var(--background);
  border: 0.0625rem solid var(--border);
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1.5rem rgb(0 0 0 / 0.24);
  display: flex;
  flex-direction: column;
  height: ${({ $height }) => $height}px;
  left: ${({ $left }) => $left}px;
  max-height: calc(100vh - ${POPUP_VIEWPORT_MARGIN_PX * 2}px);
  max-width: calc(100vw - ${POPUP_VIEWPORT_MARGIN_PX * 2}px);
  min-height: min(
    ${MIN_POPUP_HEIGHT_PX}px,
    calc(100vh - ${POPUP_VIEWPORT_MARGIN_PX * 2}px)
  );
  min-width: min(
    ${MIN_POPUP_WIDTH_PX}px,
    calc(100vw - ${POPUP_VIEWPORT_MARGIN_PX * 2}px)
  );
  overflow: hidden;
  pointer-events: auto;
  position: fixed;
  top: ${({ $top }) => $top}px;
  width: ${({ $width }) => $width}px;
  z-index: 10000;
`

const BugReportDialogHeader = styled.header`
  align-items: flex-start;
  border-bottom: 0.0625rem solid var(--border);
  cursor: grab;
  display: flex;
  flex: 0 0 auto;
  gap: 1rem;
  padding: 1.5rem;
  touch-action: none;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
`

const BugReportDialogIcon = styled.div`
  align-items: center;
  background: var(--muted);
  border-radius: 0.5rem;
  color: var(--foreground);
  display: flex;
  flex: 0 0 auto;
  height: 2.5rem;
  justify-content: center;
  width: 2.5rem;

  svg {
    height: 1.25rem;
    width: 1.25rem;
  }
`

const BugReportDialogTitleGroup = styled.div`
  display: grid;
  flex: 1 1 auto;
  gap: 0.375rem;
  min-width: 0;
  padding-right: 2rem;
`

const CloseButtonPosition = styled.div`
  position: absolute;
  right: 1rem;
  top: 1rem;
`

const HeaderShell = styled.div`
  flex: 0 0 auto;
  position: relative;
`

const BugReportDialogBody = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
  overflow-y: auto;
  padding: 1.5rem;
`

const TextareaContainer = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-height: 8rem;
  min-width: 0;
  width: 100%;
`

const BugReportDialogFooter = styled.footer`
  border-top: 0.0625rem solid var(--border);
  display: grid;
  flex: 0 0 auto;
  gap: 0.75rem;
  padding: 1.5rem;
`

const FooterActions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
`

const resizeHandlePositions = {
  n: css`
    top: 0;
    left: 0.75rem;
    right: 0.75rem;
    height: 0.5rem;
  `,
  ne: css`
    top: 0;
    right: 0;
    width: 0.75rem;
    height: 0.75rem;
  `,
  e: css`
    top: 0.75rem;
    bottom: 0.75rem;
    right: 0;
    width: 0.5rem;
  `,
  se: css`
    bottom: 0;
    right: 0;
    width: 0.75rem;
    height: 0.75rem;
  `,
  s: css`
    bottom: 0;
    left: 0.75rem;
    right: 0.75rem;
    height: 0.5rem;
  `,
  sw: css`
    bottom: 0;
    left: 0;
    width: 0.75rem;
    height: 0.75rem;
  `,
  w: css`
    top: 0.75rem;
    bottom: 0.75rem;
    left: 0;
    width: 0.5rem;
  `,
  nw: css`
    top: 0;
    left: 0;
    width: 0.75rem;
    height: 0.75rem;
  `,
}

const ResizeHandle = styled.div<{ $direction: ResizeDirection }>`
  position: absolute;
  touch-action: none;
  z-index: 1;
  cursor: ${({ $direction }) => $direction}-resize;
  ${({ $direction }) => resizeHandlePositions[$direction]}
`

export function DocumentsBugReportButton({
  buildContext,
  onRecordAction,
  onSubmitSuccess,
  // eslint-disable-next-line lingui/no-unlocalized-strings
  openActionLabel = 'Opened bug report from documents page',
  dialogTitle,
  renderTrigger,
}: DocumentsBugReportButtonProps) {
  const { t, i18n } = useLingui()
  const { FORECAST_VERSION: forecastVersion, POWER_BACKEND_URL } =
    useRuntimeVariables()
  const { authorizedAxios } = useGeckoAuth()
  const { hasPermissions } = useGeckoPermissions()
  const isInternalMember = hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER])

  const [isOpen, setIsOpen] = useState(false)
  const [context, setContext] = useState<ViewerBugReportContext | null>(null)
  const [body, setBody] = useState('')
  const submittingRef = useRef(false)
  const [priority, setPriority] = useState<BugReportPriority>(
    DEFAULT_BUG_REPORT_CLASSIFICATION.priority
  )
  const [type, setType] = useState<BugReportType>(
    DEFAULT_BUG_REPORT_CLASSIFICATION.type
  )
  const [agentMode, setAgentMode] = useState<BugReportAgentMode>(
    DEFAULT_BUG_REPORT_CLASSIFICATION.agentMode
  )
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const {
    screenshots,
    operation: screenshotOperation,
    error: screenshotError,
    addFiles,
    remove: removeScreenshot,
    reset: resetScreenshots,
  } = useBugReportScreenshots()
  const handleClose = useCallback(() => {
    if (submittingRef.current) return
    resetScreenshots()
    setIsOpen(false)
  }, [resetScreenshots])
  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLDivElement>) => {
      if (isSubmitting || screenshotOperation !== null) return
      const files = screenshotFilesFromClipboard(event.clipboardData.items)
      if (files.length > 0) {
        event.preventDefault()
        void addFiles(files)
      }
    },
    [addFiles, isSubmitting, screenshotOperation]
  )
  const [layout, setLayout] = useState<PopupLayout>(() =>
    typeof window === 'undefined'
      ? {
          left: POPUP_VIEWPORT_MARGIN_PX,
          top: POPUP_VIEWPORT_MARGIN_PX,
          width: DEFAULT_POPUP_WIDTH_PX,
          height: DEFAULT_POPUP_HEIGHT_PX,
        }
      : getDefaultPopupLayout(DEFAULT_POPUP_WIDTH_PX, DEFAULT_POPUP_HEIGHT_PX)
  )
  const dragSessionRef = useRef<{
    originLeft: number
    originTop: number
    startClientX: number
    startClientY: number
  } | null>(null)
  const resizeSessionRef = useRef<{
    origin: PopupLayout
    direction: ResizeDirection
    startClientX: number
    startClientY: number
  } | null>(null)

  useEffect(() => {
    if (!isOpen) {
      dragSessionRef.current = null
      resizeSessionRef.current = null
      return
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [handleClose, isOpen])

  const handleOpen = useCallback(() => {
    resetScreenshots()
    onRecordAction?.(openActionLabel)
    setContext({
      ...buildContext(openActionLabel),
      forecastVersion,
    })
    setBody(buildViewerBugReportUserBody())
    setPriority(DEFAULT_BUG_REPORT_CLASSIFICATION.priority)
    setType(DEFAULT_BUG_REPORT_CLASSIFICATION.type)
    setAgentMode(DEFAULT_BUG_REPORT_CLASSIFICATION.agentMode)
    setError(null)
    setLayout(
      getDefaultPopupLayout(DEFAULT_POPUP_WIDTH_PX, DEFAULT_POPUP_HEIGHT_PX)
    )
    setIsOpen(true)
  }, [
    buildContext,
    resetScreenshots,
    forecastVersion,
    onRecordAction,
    openActionLabel,
  ])

  const handleHeaderPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (event.button !== 0) return
      dragSessionRef.current = {
        originLeft: layout.left,
        originTop: layout.top,
        startClientX: event.clientX,
        startClientY: event.clientY,
      }
      event.currentTarget.setPointerCapture(event.pointerId)
    },
    [layout.left, layout.top]
  )

  const handleHeaderPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const session = dragSessionRef.current
      if (!session) return
      const nextLeft = computePointerDelta(
        session.originLeft,
        session.startClientX,
        event.clientX
      )
      const nextTop = computePointerDelta(
        session.originTop,
        session.startClientY,
        event.clientY
      )
      setLayout((current) => ({
        ...current,
        left: clamp(
          nextLeft,
          POPUP_VIEWPORT_MARGIN_PX,
          window.innerWidth - current.width - POPUP_VIEWPORT_MARGIN_PX
        ),
        top: clamp(
          nextTop,
          POPUP_VIEWPORT_MARGIN_PX,
          window.innerHeight - current.height - POPUP_VIEWPORT_MARGIN_PX
        ),
      }))
    },
    []
  )

  const handleHeaderPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!dragSessionRef.current) return
      dragSessionRef.current = null
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }
    },
    []
  )

  const handleResizePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (event.button !== 0) return
      const direction = RESIZE_DIRECTIONS.find(
        (value) => value === event.currentTarget.dataset.bugReportResize
      )
      if (!direction) return
      event.preventDefault()
      event.stopPropagation()
      resizeSessionRef.current = {
        origin: layout,
        direction,
        startClientX: event.clientX,
        startClientY: event.clientY,
      }
      event.currentTarget.setPointerCapture(event.pointerId)
    },
    [layout]
  )

  const handleResizePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const session = resizeSessionRef.current
      if (!session) return
      setLayout(
        resizeBugReportPopup(
          session.origin,
          session.direction,
          event.clientX - session.startClientX,
          event.clientY - session.startClientY,
          {
            width: window.innerWidth,
            height: window.innerHeight,
            margin: POPUP_VIEWPORT_MARGIN_PX,
            minWidth: MIN_POPUP_WIDTH_PX,
            minHeight: MIN_POPUP_HEIGHT_PX,
          }
        )
      )
    },
    []
  )

  const handleResizePointerUp = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!resizeSessionRef.current) return
      resizeSessionRef.current = null
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }
    },
    []
  )

  const handleSubmit = useCallback(async () => {
    if (!context || submittingRef.current || screenshotOperation !== null)
      return
    submittingRef.current = true

    setIsSubmitting(true)
    setError(null)

    try {
      const payload = buildViewerBugReportSlackPayload(context, body, {
        priority: isInternalMember
          ? priority
          : DEFAULT_BUG_REPORT_CLASSIFICATION.priority,
        type,
        agentMode: isInternalMember
          ? agentMode
          : DEFAULT_BUG_REPORT_CLASSIFICATION.agentMode,
      })
      const path = screenshots.length > 0 ? 'slack/with-screenshots' : 'slack'
      await authorizedAxios.post(
        `${POWER_BACKEND_URL}/api/v2/document-viewer/bug-reports/${path}`,
        { ...payload, screenshots: screenshots.map(screenshotPayload) },
        { headers: { 'Content-Type': 'application/json' } }
      )
      setIsOpen(false)
      resetScreenshots()
      onSubmitSuccess?.()
    } catch {
      setError(
        screenshots.length > 0
          ? t`Could not send the report with its screenshots. Please retry, or remove the screenshots to send a text-only report.`
          : t`Could not send the issue report. Please try again.`
      )
    } finally {
      submittingRef.current = false
      setIsSubmitting(false)
    }
  }, [
    POWER_BACKEND_URL,
    agentMode,
    authorizedAxios,
    body,
    context,
    isInternalMember,
    onSubmitSuccess,
    priority,
    resetScreenshots,
    screenshotOperation,
    screenshots,
    t,
    type,
  ])

  const title = dialogTitle ?? t`Report Issue`

  return (
    <>
      {renderTrigger ? (
        renderTrigger({ disabled: isSubmitting, onClick: handleOpen })
      ) : (
        <Button
          variant='inputRaised'
          size='toolbar'
          onClick={handleOpen}
          disabled={isSubmitting}
          title={t`Report Issue`}>
          <Flag size={18} strokeWidth={1.5} />
          <span>{t`Report Issue`}</span>
        </Button>
      )}
      {isOpen
        ? createPortal(
            <FloatingBugReportPopup
              role='dialog'
              aria-modal='false'
              aria-label={title}
              data-slot='bug-report-floating-popup'
              data-html2canvas-ignore
              onPaste={handlePaste}
              $left={layout.left}
              $top={layout.top}
              $width={layout.width}
              $height={layout.height}>
              <HeaderShell>
                <BugReportDialogHeader
                  onPointerDown={handleHeaderPointerDown}
                  onPointerMove={handleHeaderPointerMove}
                  onPointerUp={handleHeaderPointerUp}
                  onPointerCancel={handleHeaderPointerUp}>
                  <BugReportDialogIcon>
                    <Flag />
                  </BugReportDialogIcon>
                  <BugReportDialogTitleGroup>
                    <Text as='h2' size='sm' weight='semibold'>
                      {title}
                    </Text>
                    <Text as='p' size='sm' color='muted'>
                      {t`Send a message to the Gecko Support Team.`}
                    </Text>
                  </BugReportDialogTitleGroup>
                </BugReportDialogHeader>
                <CloseButtonPosition>
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon-sm'
                    aria-label={t`Close`}
                    disabled={isSubmitting}
                    onClick={handleClose}>
                    <X />
                  </Button>
                </CloseButtonPosition>
              </HeaderShell>
              <BugReportDialogBody>
                {isInternalMember ? (
                  <BugReportPriorityField
                    value={priority}
                    disabled={isSubmitting}
                    onChange={setPriority}
                  />
                ) : null}
                <BugReportTypeField
                  value={type}
                  disabled={isSubmitting}
                  onChange={setType}
                />
                {isInternalMember ? (
                  <BugReportAgentModeField
                    value={agentMode}
                    disabled={isSubmitting}
                    onChange={setAgentMode}
                  />
                ) : null}
                <TextareaContainer>
                  <Textarea
                    sizing='fill'
                    value={body}
                    onChange={(event) => setBody(event.target.value)}
                    disabled={isSubmitting}
                    aria-label={i18n._(BUG_REPORT_TYPE_INPUT_LABELS[type])}
                    placeholder={i18n._(BUG_REPORT_TYPE_PLACEHOLDERS[type])}
                  />
                </TextareaContainer>
                <BugReportScreenshots
                  screenshots={screenshots}
                  operation={screenshotOperation}
                  error={screenshotError}
                  disabled={isSubmitting}
                  onFiles={addFiles}
                  onRemove={removeScreenshot}
                />
              </BugReportDialogBody>
              <BugReportDialogFooter>
                {error ? (
                  <Text as='p' size='sm' color='destructive' role='alert'>
                    {error}
                  </Text>
                ) : null}
                <FooterActions>
                  <Button
                    type='button'
                    variant='inputRaised'
                    size='lg'
                    onClick={handleClose}
                    disabled={isSubmitting}>
                    {t`Cancel`}
                  </Button>
                  <Button
                    type='button'
                    size='lg'
                    onClick={handleSubmit}
                    disabled={
                      isSubmitting ||
                      screenshotOperation !== null ||
                      body.trim().length === 0
                    }>
                    {isSubmitting && <Spinner size={16} />}
                    {isSubmitting ? t`Submitting...` : t`Submit`}
                  </Button>
                </FooterActions>
              </BugReportDialogFooter>
              {RESIZE_DIRECTIONS.map((direction) => (
                <ResizeHandle
                  key={direction}
                  $direction={direction}
                  aria-hidden='true'
                  data-bug-report-resize={direction}
                  onPointerDown={handleResizePointerDown}
                  onPointerMove={handleResizePointerMove}
                  onPointerUp={handleResizePointerUp}
                  onPointerCancel={handleResizePointerUp}
                  onLostPointerCapture={handleResizePointerUp}
                />
              ))}
            </FloatingBugReportPopup>,
            document.body
          )
        : null}
    </>
  )
}
